import React, { cloneElement, useEffect, useMemo } from 'react'
import { Spinner } from 'gu-ui-kit'
import format from 'utils/dateFns/format'
import PropTypes from 'prop-types'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { Line } from 'components/Chart/Chart/Line'
import { Bar } from 'components/Chart/Chart/Bar'
import { ChartLegend } from 'components/Chart/ChartLegend'
import { FORMAT_PERIOD } from 'modules/Statistics/Pulse/constants/formatPeriod'
import styles from 'components/Chart/Chart/Chart.module.scss'
import clsx from 'clsx'
import ChartTypeSwitcher from 'components/Chart/ChartTypeSwitcher'
import DateGroupSwitcher from 'components/Chart/DateGroupSwitcher'
import EmptyResults from 'components/EmptyResults'
import { ReactComponent as EmptyChartImage } from 'images/illustration/empty-chart.svg'

export const Chart = ({
  className = '',
  chartHeaderClassName = '',
  chartType,
  items,
  metrics,
  loading,
  date_group_period,
  include_vat,
  summary,
  measure,
  toggleMetric,
  changeChartPeriod,
  changeChartType,
  chartLegend,
  colors,
  emptyDataLabel = {},
}) => {
  const MAX_CHART_LABEL_SCROLL = 5

  const breakpoints = useBreakpoint()
  const isMobile = breakpoints.sm

  const ChartType = useMemo(() => (chartType === 'Line' ? Line : Bar), [chartType])

  const chartLabel = items.map((item) => {
    const label = format(new Date(item.date), FORMAT_PERIOD[date_group_period.value])
    return isMobile ? label.split(' ') : label
  })

  const activeMetrics = useMemo(() => metrics.filter((metric) => metric.isActive), [metrics])

  const isDataError = useMemo(
    () => !loading && (!summary || (summary && !Object.keys(summary).length)),
    [loading, summary],
  )

  useEffect(() => {
    const tooltip = document.getElementById('chart-tooltip')
    if (tooltip) {
      tooltip.remove()
    }
  }, [loading, chartType])

  return (
    <div
      className={clsx(styles['chart'], className, {
        [styles['chart__empty']]: items.length === 0,
      })}
    >
      {items.length > 0 && (
        <div className={clsx(styles['chart__header'], chartHeaderClassName)}>
          {chartLegend ? (
            cloneElement(chartLegend, { colors, loading })
          ) : (
            <ChartLegend
              loading={loading}
              colors={colors}
              className={styles['chart__legend']}
              metrics={activeMetrics}
              handleClick={toggleMetric}
            />
          )}

          <DateGroupSwitcher
            className={styles['chart__settings']}
            handleClick={changeChartPeriod}
            dateGroupPeriod={date_group_period}
          />

          <ChartTypeSwitcher
            className={styles['chart__type-switcher']}
            chartType={chartType}
            handleClick={changeChartType}
          />
        </div>
      )}
      {isDataError ? (
        <EmptyResults
          image={<EmptyChartImage />}
          {...emptyDataLabel}
          className={styles['chart__empty-results']}
        />
      ) : (
        <div className={clsx(styles.chart__scroll)}>
          {loading ? (
            <Spinner className={styles.chart__spinner} color="Label-GU-Primary" />
          ) : (
            <div
              className={clsx(styles.chart__wrapper, {
                [styles.chart__wrapper_longer]: chartLabel.length > MAX_CHART_LABEL_SCROLL,
              })}
            >
              <ChartType
                include_vat={include_vat}
                items={items}
                metrics={activeMetrics}
                labels={chartLabel}
                measure={measure}
                isMobile={isMobile}
                colors={colors}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

Chart.defaultProps = {
  className: '',
  chartHeaderClassName: '',
  chartType: 'Line',
  items: [],
  metrics: [],
  loading: false,
  date_group_period: {},
  include_vat: true,
  summary: [],
  measure: [],
  chartLegend: null,
  colors: [],
}

Chart.propTypes = {
  className: PropTypes.string,
  chartHeaderClassName: PropTypes.string,
  chartType: PropTypes.string,
  items: PropTypes.array,
  metrics: PropTypes.array,
  loading: PropTypes.bool,
  date_group_period: PropTypes.object,
  include_vat: PropTypes.bool,
  summary: PropTypes.object,
  measure: PropTypes.object,
  emptyDataLabel: PropTypes.object,
  toggleMetric: PropTypes.func,
  changeChartPeriod: PropTypes.func,
  changeChartType: PropTypes.func,
  chartLegend: PropTypes.element,
  colors: PropTypes.array,
}
